import { Link } from "react-router-dom";
import {
  Shield,
  Mail,
  Clock,
  ChevronLeft,
  Lock,
  Database,
  UserCheck,
  Share2,
  RefreshCw,
  AlertTriangle,
  Sparkles,
  LifeBuoy,
  CreditCard,
  Globe2,
  Scale,
} from "lucide-react";
import SiteFooter from "./SiteFooter";

const lastUpdated = "January 2026";

const sections = [
  {
    id: "info-we-collect",
    icon: Database,
    title: "Information We Collect",
    body: [
      "Account data you provide directly: hunter name, email address, and authentication credentials.",
      "Gameplay data generated as you forge progress: quests completed, stats, levels, streaks, and in-app preferences.",
      "Device & technical data: device model, OS version, app version, crash logs, and anonymous usage analytics used to improve performance.",
    ],
  },
  {
    id: "how-we-use",
    icon: UserCheck,
    title: "How We Use Your Data",
    body: [
      "To operate core features: syncing progression, persisting your forge, and unlocking content you have earned.",
      "To improve Arise Forge through aggregated analytics, bug diagnostics, and balancing of in-app systems.",
      "To communicate essential service updates, security notices, and responses to your support requests.",
    ],
  },
  {
    id: "storage",
    icon: Lock,
    title: "Data Storage & Security",
    body: [
      "All credentials are hashed and never stored in plain text. Sensitive traffic is encrypted in transit (TLS).",
      "Backups are encrypted at rest and access is restricted to authorized maintainers under least-privilege controls.",
      "While we apply strong industry practices, no system is unbreakable — we will notify affected users promptly if a breach materially impacts their account.",
    ],
  },
  {
    id: "purchases",
    icon: CreditCard,
    title: "In-App Purchases & Payments",
    body: [
      "Arise Forge offers optional in-app purchases (e.g., cosmetic items, premium features, or consumables). All payments are processed exclusively through the Apple App Store (StoreKit) on iOS and the Google Play Store (Google Play Billing) on Android.",
      "We never see, receive, or store your full payment details. Card numbers, billing addresses, and bank information are handled by Apple and Google under their own privacy policies and security standards.",
      "We do receive a non-sensitive transaction confirmation (e.g., a purchase token, product ID, transaction timestamp, and receipt) so we can unlock the content you purchased and validate the purchase to prevent fraud.",
      "Refund requests for in-app purchases are governed by Apple's and Google's policies — please request refunds through your App Store or Google Play account. Our support team can help you locate the right form.",
      "If you restore purchases on a new device, Apple/Google will share your past entitlements with the app so we can re-grant the content you already own.",
    ],
  },
  {
    id: "sharing",
    icon: Share2,
    title: "Sharing With Third Parties",
    body: [
      "We do not sell your personal data. Period.",
      "Apple Inc. and Google LLC act as payment processors for in-app purchases and receive the data necessary to complete and validate transactions.",
      "Limited operational data may be processed by trusted infrastructure providers (hosting, crash reporting, anonymous analytics) strictly to operate the service, under contractual privacy obligations.",
      "We may disclose information when legally required (e.g., valid court order) or to protect the rights, property, or safety of users and the service.",
    ],
  },
  {
    id: "rights",
    icon: Shield,
    title: "Your Rights & Choices",
    body: [
      "Access — request a copy of the personal data we hold about you.",
      "Correction — update inaccurate or incomplete information from within the app or via support.",
      "Deletion — request permanent deletion of your account and associated forge data.",
      "Opt-out — disable non-essential analytics from in-app settings at any time.",
      "To exercise any of these rights, email us at ariseforgeapp@gmail.com — we respond within 30 days.",
    ],
  },
  {
    id: "gdpr",
    icon: Globe2,
    title: "Rights for EU / UK / EEA Residents (GDPR)",
    body: [
      "If you reside in the European Economic Area, the United Kingdom, or Switzerland, you have additional rights under the GDPR / UK GDPR.",
      "Lawful basis — we process your data on the basis of (a) contractual necessity to deliver the app, (b) your consent for non-essential analytics, and (c) our legitimate interests in securing and improving the service.",
      "You have the right to access, rectify, erase, restrict, or port your data, and to object to processing. You may also withdraw consent at any time without affecting prior lawful processing.",
      "International transfers — your data may be processed in the United States or other countries. We rely on Standard Contractual Clauses or equivalent safeguards where required.",
      "You have the right to lodge a complaint with your local supervisory authority (e.g., your national Data Protection Authority).",
    ],
  },
  {
    id: "ccpa",
    icon: Scale,
    title: "Rights for California Residents (CCPA / CPRA)",
    body: [
      "If you are a California resident, the CCPA / CPRA gives you specific rights regarding your personal information.",
      "Right to know — what categories of personal information we collect, the sources, the purposes, and the categories of third parties we share it with.",
      "Right to delete — request deletion of personal information we have collected, subject to legal exceptions.",
      "Right to correct — request correction of inaccurate personal information.",
      "Right to opt-out of sale or sharing — Arise Forge does not sell or share personal information for cross-context behavioral advertising. There is nothing to opt out of, but the right is preserved.",
      "Non-discrimination — we will not discriminate against you for exercising any of these rights.",
      "To submit a verifiable consumer request, email ariseforgeapp@gmail.com with the subject \"CCPA Request\".",
    ],
  },
  {
    id: "children",
    icon: AlertTriangle,
    title: "Children's Privacy",
    body: [
      "Arise Forge is not directed to children under 13. We do not knowingly collect personal data from children under 13.",
      "If you believe a child has provided us information, contact us and we will remove it promptly.",
    ],
  },
  {
    id: "account-deletion",
    icon: AlertTriangle,
    title: "Account Deletion",
    body: [
      "You may request deletion of your Arise Forge account and all associated data at any time.",
      "To request deletion, email ariseforgeapp@gmail.com from the email associated with your account.",
      "Account deletion requests are typically processed within 30 days.",
    ],
  },
  {
    id: "changes",
    icon: RefreshCw,
    title: "Changes to This Policy",
    body: [
      "We may update this policy as Arise Forge evolves. Material changes will be highlighted in-app or by email when appropriate.",
      "The “Last Updated” timestamp at the top of this page always reflects the current effective version.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="relative z-10 min-h-screen">
      {/* Top bar */}
      <header className="relative z-10 border-b border-[rgba(76,201,255,0.18)] bg-[rgba(4,6,13,0.55)] backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link
            to="/"
            data-testid="back-home-link"
            className="link-neon group inline-flex items-center gap-2 font-system text-xs uppercase tracking-[0.28em]"
          >
            <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Return to Forge
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden h-2 w-2 rotate-45 bg-[var(--neon)] shadow-[0_0_10px_rgba(76,201,255,0.8)] sm:inline-block" />
            <span className="font-fantasy text-xs tracking-[0.4em] text-[var(--neon-bright)] sm:text-sm">
              ARISE FORGE
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-6 pb-10 pt-16 sm:pt-24">
        <div className="mx-auto max-w-4xl text-center">
          <div className="animate-drift-up flex justify-center">
            <span data-testid="hero-badge" className="badge-system animate-pulse-glow">
              <Sparkles className="h-3 w-3" />
              System Notice
            </span>
          </div>

          <h1
            data-testid="page-title"
            className="animate-drift-up font-fantasy mt-6 text-4xl leading-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "80ms" }}
          >
            <span className="block neon-text">Privacy Policy</span>
            <span className="mt-2 block text-[var(--ink)]/70 text-2xl tracking-[0.3em] sm:text-3xl">
              &amp; SUPPORT
            </span>
          </h1>

          <p
            className="animate-drift-up mx-auto mt-6 max-w-2xl font-system text-base leading-relaxed text-[var(--ink-dim)] sm:text-lg"
            style={{ animationDelay: "160ms" }}
          >
            Transparency is part of the contract. Below are the rules that govern your data,
            and the runes that summon our support team when you need help on your journey.
          </p>

          <div
            className="animate-drift-up mt-6 flex flex-wrap items-center justify-center gap-3 font-system text-xs uppercase tracking-[0.22em] text-[var(--ink-dim)]"
            style={{ animationDelay: "220ms" }}
          >
            <span className="inline-flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-[var(--neon)]" />
              Last updated · {lastUpdated}
            </span>
            <span className="hidden h-3 w-px bg-[rgba(76,201,255,0.35)] sm:block" />
            <span>Effective immediately</span>
          </div>

          <div className="divider-glow mx-auto mt-10 w-3/4 max-w-md" />
        </div>
      </section>

      {/* Content */}
      <section className="relative px-6 pb-16">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="panel sticky top-8 p-5">
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />
              <h2 className="font-fantasy text-xs tracking-[0.32em] text-[var(--neon-bright)]">
                INDEX
              </h2>
              <div className="divider-glow my-3" />
              <ul className="space-y-2 font-system text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`toc-${s.id}`}
                      className="link-neon block py-1 leading-snug text-[var(--ink)] hover:text-white"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#support"
                    data-testid="toc-support"
                    className="link-neon block py-1 leading-snug text-[var(--ink)] hover:text-white"
                  >
                    Support &amp; Contact
                  </a>
                </li>
              </ul>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-6">
            <article
              data-testid="privacy-intro-panel"
              className="panel relative overflow-hidden p-6 sm:p-8"
            >
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />
              <div className="flex items-start gap-4">
                <div className="hidden h-12 w-12 items-center justify-center border border-[rgba(76,201,255,0.5)] bg-[rgba(76,201,255,0.08)] text-[var(--neon-bright)] shadow-[0_0_18px_rgba(76,201,255,0.25)] sm:flex">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-fantasy text-xl tracking-[0.18em] text-white sm:text-2xl">
                    The Pact
                  </h2>
                  <p className="mt-3 font-system text-base leading-relaxed text-[var(--ink)]">
                    Welcome, Hunter. This Privacy Policy explains what data Arise Forge collects,
                    why we collect it, how in-app purchases are handled, and the rights you have
                    over your information. By using the app you accept this pact. If you do not
                    agree, please refrain from using Arise Forge.
                  </p>
                </div>
              </div>
            </article>

            {sections.map((s, idx) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.id}
                  id={s.id}
                  data-testid={`section-${s.id}`}
                  className="panel relative scroll-mt-24 overflow-hidden p-6 sm:p-8"
                >
                  <span className="corner-tl" />
                  <span className="corner-tr" />
                  <span className="corner-bl" />
                  <span className="corner-br" />
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-[rgba(76,201,255,0.5)] bg-[rgba(76,201,255,0.08)] text-[var(--neon-bright)] shadow-[0_0_14px_rgba(76,201,255,0.25)]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-3">
                        <span className="font-fantasy text-xs tracking-[0.32em] text-[var(--neon)]">
                          §{String(idx + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-fantasy text-lg tracking-[0.16em] text-white sm:text-xl">
                          {s.title}
                        </h3>
                      </div>
                      <ul className="mt-4 space-y-3 font-system text-base leading-relaxed text-[var(--ink)]">
                        {s.body.map((line, i) => (
                          <li key={i} className="flex items-start">
                            <span className="rune-bullet mt-2" aria-hidden="true" />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );
            })}

            {/* Support panel */}
            <article
              id="support"
              data-testid="support-panel"
              className="panel relative scroll-mt-24 overflow-hidden p-6 sm:p-8"
            >
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />

              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-[rgba(76,201,255,0.5)] bg-[rgba(76,201,255,0.08)] text-[var(--neon-bright)] shadow-[0_0_14px_rgba(76,201,255,0.25)]">
                    <LifeBuoy className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="badge-system">Support Channel</span>
                    <h3 className="font-fantasy mt-3 text-2xl tracking-[0.18em] text-white sm:text-3xl">
                      <span className="neon-text">SUMMON</span> ASSISTANCE
                    </h3>
                    <p className="mt-3 font-system text-base leading-relaxed text-[var(--ink)]">
                      Stuck on a quest? Found a bug in the system? Need account help?
                      Our support team is the fastest path to a fix. Reach out and we will
                      respond personally.
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Email card */}
                  <a
                    href="mailto:ariseforgeapp@gmail.com"
                    data-testid="support-email-link"
                    className="group relative block border border-[rgba(76,201,255,0.45)] bg-[rgba(76,201,255,0.06)] p-5 transition-all hover:border-[var(--neon-bright)] hover:bg-[rgba(76,201,255,0.12)] hover:shadow-[0_0_24px_rgba(76,201,255,0.45)]"
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-[var(--neon-bright)]" />
                      <span className="font-fantasy text-xs tracking-[0.28em] text-[var(--neon-bright)]">
                        EMAIL US
                      </span>
                    </div>
                    <p
                      data-testid="support-email"
                      className="mt-3 break-all font-system text-lg font-semibold text-white transition-colors group-hover:text-[var(--neon-bright)] sm:text-xl"
                    >
                      ariseforgeapp@gmail.com
                    </p>
                    <p className="mt-2 font-system text-xs uppercase tracking-[0.22em] text-[var(--ink-dim)]">
                      Tap to open your mail client
                    </p>
                  </a>

                  {/* Response time card */}
                  <div className="relative block border border-[rgba(76,201,255,0.25)] bg-[rgba(7,11,24,0.7)] p-5">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-[var(--neon-bright)]" />
                      <span className="font-fantasy text-xs tracking-[0.28em] text-[var(--neon-bright)]">
                        RESPONSE WINDOW
                      </span>
                    </div>
                    <p className="mt-3 font-system text-lg font-semibold text-white sm:text-xl">
                      Within 24 – 48 hours
                    </p>
                    <p className="mt-2 font-system text-xs uppercase tracking-[0.22em] text-[var(--ink-dim)]">
                      Mon – Fri, longer on weekends &amp; holidays
                    </p>
                  </div>
                </div>

                <div className="border border-dashed border-[rgba(76,201,255,0.3)] bg-[rgba(76,201,255,0.04)] p-5">
                  <h4 className="font-fantasy text-xs tracking-[0.32em] text-[var(--neon-bright)]">
                    QUEST CHECKLIST · BEFORE YOU CONTACT US
                  </h4>
                  <ul
                    data-testid="support-checklist"
                    className="mt-4 space-y-2 font-system text-base text-[var(--ink)]"
                  >
                    <li className="flex items-start">
                      <span className="rune-bullet mt-2" aria-hidden="true" />
                      <span>Include your <strong className="text-white">hunter name</strong> and the email tied to your account.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="rune-bullet mt-2" aria-hidden="true" />
                      <span>Describe the issue and the <strong className="text-white">steps to reproduce</strong> it.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="rune-bullet mt-2" aria-hidden="true" />
                      <span>Attach <strong className="text-white">screenshots</strong> or short clips when possible — they speed up triage.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="rune-bullet mt-2" aria-hidden="true" />
                      <span>Tell us your <strong className="text-white">device model</strong> and <strong className="text-white">app version</strong>.</span>
                    </li>
                  </ul>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="mailto:ariseforgeapp@gmail.com?subject=Arise%20Forge%20%E2%80%94%20Support%20Request"
                    data-testid="support-cta"
                    className="btn-system"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Open Support Ticket
                  </a>
                  <span className="font-system text-xs uppercase tracking-[0.22em] text-[var(--ink-dim)]">
                    or copy: <span className="text-[var(--neon-bright)]">ariseforgeapp@gmail.com</span>
                  </span>
                </div>
              </div>
            </article>

            {/* Closing note */}
            <article
              data-testid="closing-panel"
              className="panel relative overflow-hidden p-6 text-center sm:p-8"
            >
              <span className="corner-tl" />
              <span className="corner-tr" />
              <span className="corner-bl" />
              <span className="corner-br" />
              <p className="font-fantasy text-sm tracking-[0.3em] text-[var(--neon-bright)]">
                ARISE, HUNTER
              </p>
              <p className="mt-3 font-system text-base text-[var(--ink-dim)]">
                Your data, your forge. We are honored to walk this path with you.
              </p>
            </article>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
